import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

export type MultiSelectOption = { value: string; label: string };

export function MultiSelect({
  id, values, onChange, options, placeholder,
}: {
  id?: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedLabels = options.filter((o) => values.includes(o.value)).map((o) => o.label);
  const trimmed = query.trim();
  const filtered = trimmed ? options.filter((o) => o.label.toLowerCase().includes(trimmed.toLowerCase())) : options;

  function toggle(value: string) {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
  }

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setQuery('');
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          id={id}
          type="button"
          className="flex h-10 w-full items-center justify-between gap-2 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm text-ink outline-none focus-visible:border-accent"
        >
          <span className={cn('truncate', selectedLabels.length === 0 && 'text-muted')}>
            {selectedLabels.length === 0
              ? (placeholder ?? t('combobox.select'))
              : selectedLabels.length === 1
                ? selectedLabels[0]
                : t('combobox.selectedCount', { count: selectedLabels.length })}
          </span>
          <ChevronDown size={14} className="shrink-0 text-muted" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] rounded-[var(--radius-card)] border border-border bg-surface shadow-md"
        >
          <div className="border-b border-border p-1.5">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('combobox.search')}
              className="h-8 w-full rounded-[var(--radius-input)] border border-border bg-surface-2 px-2 text-sm text-ink outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto p-1">
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-surface-2"
              >
                <Check size={13} className={values.includes(o.value) ? 'opacity-100' : 'opacity-0'} />
                <span className="truncate">{o.label}</span>
              </button>
            ))}
            {filtered.length === 0 ? <p className="px-2 py-1.5 text-xs text-muted">{t('combobox.nothingFound')}</p> : null}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
